import React, { useState, useEffect, useRef, useCallback } from "react";
import { useGlobalLoader } from "../../context/GlobalLoaderContext";
import { addAdmitCard, deleteAdmitCard, getJobs, API } from "../../api/api";
import { useGlobalData } from "../../context/GlobalDataContext";
import "../superadmin/AdminPanel.css";

function HrAdmitCardAdminPanel({ darkMode }) {
  const { loading: globalLoading } = useGlobalLoader();
  const { threeTable, fetchThreeTableSection } = useGlobalData();
  const [admitCards, setAdmitCards] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [admitCardsOffset, setAdmitCardsOffset] = useState(0);
  const [admitCardsHasMore, setAdmitCardsHasMore] = useState(true);
  const [isFetchingMore, setIsFetchingMore] = useState(false);
  // Remove local isLoading, use global loader
  const listRef = useRef(null);
  const PAGE_LIMIT = 29;
  const [formData, setFormData] = useState({ jobSlug: "" });
  const role = localStorage.getItem("role");

  // Initial load (global state)
  useEffect(() => {
    let ignore = false;
    async function fetchInitial() {
      await fetchThreeTableSection("admitCards", false, { limit: PAGE_LIMIT, offset: 0 });
      const jobsRes = await getJobs();
      if (!ignore) setJobs(jobsRes.data);
    }
    fetchInitial();
    return () => { ignore = true; };
    // eslint-disable-next-line
  }, []);

  // Sync admitCards from global state
  useEffect(() => {
    setAdmitCards(threeTable.admitCards || []);
    setAdmitCardsOffset(threeTable.admitCards?.length || 0);
    setAdmitCardsHasMore((threeTable.admitCards?.length || 0) === PAGE_LIMIT);
  }, [threeTable.admitCards]);

  // Infinite scroll fetch
  const fetchMoreAdmitCards = useCallback(async () => {
    if (!admitCardsHasMore || isFetchingMore) return;
    setIsFetchingMore(true);
    await fetchThreeTableSection("admitCards", false, { limit: PAGE_LIMIT, offset: admitCardsOffset });
    setIsFetchingMore(false);
  }, [admitCardsHasMore, isFetchingMore, admitCardsOffset, fetchThreeTableSection]);

  // Infinite scroll handler
  const handleScroll = useCallback(() => {
    if (!admitCardsHasMore || isFetchingMore) return;
    const el = listRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    if (rect.bottom <= window.innerHeight + 200) {
      fetchMoreAdmitCards();
    }
  }, [admitCardsHasMore, isFetchingMore, fetchMoreAdmitCards]);

  useEffect(() => {
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [handleScroll]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    await addAdmitCard(formData);
    setFormData({ jobSlug: "" });
    await fetchThreeTableSection("admitCards", true, { limit: PAGE_LIMIT, offset: 0 });
  };

  const handleApprove = async (id) => {
    try {
      await API.patch(`/admitcard/${id}/approve`);
      await fetchThreeTableSection("admitCards", true, { limit: PAGE_LIMIT, offset: 0 });
    } catch (err) {
      alert("Error approving admit card");
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm("Delete this admit card?")) {
      await deleteAdmitCard(id);
      await fetchThreeTableSection("admitCards", true, { limit: PAGE_LIMIT, offset: 0 });
    }
  };

  const getJobTitle = (jobSlug) => jobs.find((job) => job.slug === jobSlug)?.title || jobSlug;
  const filteredAdmitCards = admitCards.filter(
    (admitCard) => !formData.jobSlug || admitCard.jobSlug === formData.jobSlug
  );

  if (role !== "hr") {
    return <div className="admin-panel-error">❌ Access denied! Only HR can access this panel.</div>;
  }

  return (
    <div className={`resource-admin-panel resource-admin-panel--orange${darkMode ? " dark-mode" : ""}`}> 
      <div className="resource-admin-panel__header">
        <div>
          <h2 className="resource-admin-panel__heading">HR Admit Card Admin Panel</h2>
          <p className="resource-admin-panel__subtext">Job select karke admit card entry add karo, recent entries review karo aur live page ko direct open ya delete kar sakte ho.</p>
        </div>
        <div className="resource-admin-panel__pill">Admit Card</div>
      </div>

      <form className="resource-admin-panel__form" onSubmit={handleSubmit}>
        <div className="resource-admin-panel__field">
          <label>Job:</label>
          <select
            value={formData.jobSlug}
            onChange={(e) => setFormData({ ...formData, jobSlug: e.target.value })}
            required
          >
            <option value="">Select Job</option>
            {jobs.map((job) => (
              <option key={job.slug} value={job.slug}>
                {job.title}
              </option>
            ))}
          </select>
        </div>
        <button type="submit">Add Admit Card</button>
      </form>

      <div className="resource-admin-panel__list" ref={listRef} style={{ maxHeight: "800px", overflowY: "auto" }}>
        <h3>Admit Card Entries</h3>
        {globalLoading ? (
          <div className="global-loader"><div className="skeleton-loader" /></div>
        ) : (
        <ul>
          {filteredAdmitCards.map((admitCard) => (
            <li key={admitCard._id} style={{ display: "flex", alignItems: "center", gap: "1rem" }}>
              <span>{getJobTitle(admitCard.jobSlug)}</span>
              <span style={{ fontWeight: "bold", color: admitCard.status === "approved" ? "green" : "orange" }}>
                {admitCard.status === "approved" ? "Approved" : "Pending"}
              </span>
              {admitCard.status !== "approved" && (
                <>
                  <button
                    onClick={() => handleApprove(admitCard._id)}
                    style={{
                      backgroundColor: "yellow",
                      color: "black",
                      border: "none",
                      padding: "0.3rem 0.8rem",
                      borderRadius: "5px",
                      cursor: "pointer"
                    }}
                  >
                    Approve
                  </button>
                  <button onClick={() => handleDelete(admitCard._id)} style={{ marginLeft: "0.5rem" }}>Delete</button>
                </>
              )}
            </li>
          ))}
        </ul>
        )}
        {isFetchingMore && <div style={{ textAlign: "center", margin: 10 }}>Loading more...</div>}
        {!admitCardsHasMore && !isFetchingMore && admitCards.length > 0 && <div style={{ textAlign: "center", margin: 10, color: "#888" }}>No more admit cards.</div>}
      </div>
    </div>
  );
}

export default HrAdmitCardAdminPanel;
